import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core'
import { CondOperator, RequestQueryBuilder } from '@nestjsx/crud-request'
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy'
import { Store } from '@ngrx/store'
import { BehaviorSubject, combineLatest, Observable, of } from 'rxjs'
import { map } from 'rxjs/operators'
import { AppState } from 'src/app/core/core.state'
import { settingSelectors } from 'src/app/core/settings/settings.selectors'
import { memberActions } from 'src/app/features/member/member.actions'
import { Member } from 'src/app/features/member/member'
import { memberSelectors } from 'src/app/features/member/member.selectors'
import { Answer } from 'src/app/features/survey/answer/answer'
import { questionActions } from 'src/app/features/survey/question/question.actions'
import { Question } from 'src/app/features/survey/question/question'
import { questionSelectors } from 'src/app/features/survey/question/question.selectors'
import { BulkAction, BulkActionEvent } from 'src/app/shared/interfaces'
import { getFullName } from 'src/app/shared/until'

export interface AchievementRow {
  memberId: number
  memberName: string
  answersByQuestionId: Record<number, string | null>
}

interface AchievementTableVm {
  isReady: boolean
  questions: Question[]
  rows: AchievementRow[]
}

export interface AchievementAdjustPayload {
  memberId: number
  memberName: string
  questions: Question[]
  answersByQuestionId: Record<number, string | null>
}

@UntilDestroy()
@Component({
  selector: 'achievement-table',
  templateUrl: './table.component.html',
  styleUrls: ['./table.component.scss']
})
export class AchievementTableComponent implements OnInit {
  @Input() set tableData(value: Answer[]) {
    this.tableData$.next(value || [])
  }
  @Input() set participantMemberIds(value: number[]) {
    this.participantMemberIds$.next(value || [])
  }
  @Input() set questionIds(value: number[]) {
    this.questionIds$.next(value || [])
  }
  @Input() bulkActions: BulkAction[] = []
  @Input() removeSearchTerm$: Observable<boolean> = of(false)
  @Output() onSearchTerm = new EventEmitter<string>()
  @Output() onOpenAdjust = new EventEmitter<AchievementAdjustPayload>()
  @Output() onBulkAction = new EventEmitter<BulkActionEvent>()

  public filterTerm: string = ''
  public vm$: Observable<AchievementTableVm>
  public stars: number[] = [0, 1, 2]
  public allCheckedToggle: boolean = false
  public checkedIds: Map<number, boolean> = new Map()
  private tableData$ = new BehaviorSubject<Answer[]>([])
  private participantMemberIds$ = new BehaviorSubject<number[]>([])
  private questionIds$ = new BehaviorSubject<number[]>([])
  private requestedMemberIds = new Set<number>()
  private requestedQuestionIds = new Set<number>()
  private appLanguage: string = 'en'

  constructor(private store: Store<AppState>) {}

  ngOnInit() {
    this.removeSearchTerm$.pipe(untilDestroyed(this)).subscribe((remove) => {
      if (remove) {
        this.filterTerm = ''
      }
    })

    this.store
      .select(settingSelectors.selectById, 'language')
      .pipe(untilDestroyed(this))
      .subscribe((language) => {
        this.appLanguage = language || 'en'
      })

    const memberEntities$ = this.store.select(memberSelectors.selectEntities)
    const questionEntities$ = this.store.select(
      questionSelectors.selectEntities
    )

    combineLatest([
      this.tableData$,
      this.participantMemberIds$,
      this.questionIds$,
      memberEntities$,
      questionEntities$
    ])
      .pipe(untilDestroyed(this))
      .subscribe(
        ([
          answers,
          participantMemberIds,
          questionIdsInput,
          memberEntities,
          questionEntities
        ]) => {
          const memberIds = [...new Set(participantMemberIds)]
          const questionIds = [...new Set(questionIdsInput)]

          const missingMemberIds = memberIds.filter(
            (id) => !memberEntities[id] && !this.requestedMemberIds.has(id)
          )
          if (missingMemberIds.length > 0) {
            this.store.dispatch(
              memberActions.fetchMany({
                params: RequestQueryBuilder.create({
                  search: {
                    id: {
                      [CondOperator.IN]: missingMemberIds
                    }
                  }
                })
              })
            )
            missingMemberIds.forEach((id) => this.requestedMemberIds.add(id))
          }

          const missingQuestionIds = questionIds.filter(
            (id) => !questionEntities[id] && !this.requestedQuestionIds.has(id)
          )
          if (missingQuestionIds.length > 0) {
            this.store.dispatch(
              questionActions.fetchMany({
                params: RequestQueryBuilder.create({
                  search: {
                    id: {
                      [CondOperator.IN]: missingQuestionIds
                    }
                  }
                })
              })
            )
            missingQuestionIds.forEach((id) =>
              this.requestedQuestionIds.add(id)
            )
          }
        }
      )

    this.vm$ = combineLatest([
      this.tableData$,
      this.participantMemberIds$,
      this.questionIds$,
      memberEntities$,
      questionEntities$
    ]).pipe(
      map(
        ([
          answers,
          participantMemberIds,
          questionIdsInput,
          memberEntities,
          questionEntities
        ]) => {
          const memberIds = [...new Set(participantMemberIds)]
          const questionIds = [...new Set(questionIdsInput)]
          const allMembersLoaded = memberIds.every((id) =>
            Boolean(memberEntities[id])
          )
          const allQuestionsLoaded = questionIds.every((id) =>
            Boolean(questionEntities[id])
          )
          const isReady = allMembersLoaded && allQuestionsLoaded

          if (!isReady) {
            return { isReady: false, questions: [], rows: [] }
          }

          const questions: Question[] = questionIds
            .map((id) => questionEntities[id])
            .filter(Boolean)
            .sort((a, b) => (a.ordinal ?? 0) - (b.ordinal ?? 0))

          const answersByMember = new Map<number, Answer[]>()
          answers.forEach((answer) => {
            const rows = answersByMember.get(answer.memberId) ?? []
            rows.push(answer)
            answersByMember.set(answer.memberId, rows)
          })

          const rows: AchievementRow[] = participantMemberIds
            .map((memberId) => {
              const memberAnswers = answersByMember.get(memberId) || []
              const member: Member = memberEntities[memberId]
              const answersByQuestionId: Record<number, string | null> = {}

              questions.forEach((question) => {
                const answer = memberAnswers.find(
                  (row) => row.questionId === question.id
                )
                answersByQuestionId[question.id] = answer?.result ?? null
              })

              return {
                memberId,
                memberName: getFullName(member),
                answersByQuestionId
              }
            })
            .sort((a, b) =>
              (a.memberName || '').localeCompare(b.memberName || '')
            )

          return { isReady: true, questions, rows }
        }
      )
    )
  }

  public onSearch(searchTerm: string) {
    this.filterTerm = searchTerm || ''
    this.onSearchTerm.emit(this.filterTerm)
  }

  public shouldShowRow(memberName: string): boolean {
    if (!this.filterTerm.trim()) {
      return true
    }

    return (memberName || '')
      .toLowerCase()
      .includes(this.filterTerm.toLowerCase())
  }

  public getQuestionLabel(question: Question): string {
    return (
      question?.body?.find((row) => row.language === this.appLanguage)?.text ||
      question?.body?.[0]?.text ||
      ''
    )
  }

  public getAnswerRating(
    row: AchievementRow,
    questionId: number
  ): number | null {
    const value = row.answersByQuestionId[questionId]
    if (value === undefined || value === null) {
      return null
    }

    const parsed = Number(value)
    if (!Number.isFinite(parsed)) {
      return null
    }

    if (parsed <= 0) {
      return 0
    }

    if (parsed >= this.stars.length) {
      return this.stars.length
    }

    return parsed
  }

  public openAdjustModal(row: AchievementRow, questions: Question[]): void {
    this.onOpenAdjust.emit({
      memberId: row.memberId,
      memberName: row.memberName,
      questions,
      answersByQuestionId: row.answersByQuestionId
    })
  }

  public getVisibleRows(rows: AchievementRow[]): AchievementRow[] {
    return rows.filter((row) => this.shouldShowRow(row.memberName))
  }

  public onCheckRow(targetRow: AchievementRow): void {
    this.checkedIds.set(
      targetRow.memberId,
      !Boolean(this.checkedIds.get(targetRow.memberId))
    )
  }

  public onToggleAllChecked(rows: AchievementRow[]): void {
    const visibleRows = this.getVisibleRows(rows)

    this.allCheckedToggle = !this.allCheckedToggle
    visibleRows.forEach((row) =>
      this.checkedIds.set(row.memberId, this.allCheckedToggle)
    )
  }

  public isRowChecked(row: AchievementRow): boolean {
    return Boolean(this.checkedIds.get(row.memberId))
  }

  public getCheckedRows(rows: AchievementRow[]): AchievementRow[] {
    return this.getVisibleRows(rows).filter((row) => this.isRowChecked(row))
  }

  public onClickBulkAction(action: BulkAction, rows: AchievementRow[]): void {
    const items =
      action === BulkAction.SET_ALL_TO_ACHIEVED
        ? this.getVisibleRows(rows)
        : this.getCheckedRows(rows)

    if (!items.length) {
      return
    }

    this.onBulkAction.emit({
      action,
      items
    })

    this.allCheckedToggle = false
    rows.forEach((row) => this.checkedIds.set(row.memberId, false))
  }

  public getBulkActionLabel(action: BulkAction): string {
    switch (action) {
      case BulkAction.SET_ACHIEVEMENTS:
        return 'achievements.setAchievements'
      case BulkAction.SET_ALL_TO_ACHIEVED:
        return 'achievements.setAllToAchieved'
      default:
        return 'general.' + action
    }
  }
}
